import React, { useState } from 'react';
import { FileText, Calendar, CheckCircle, FileArchive, MessageSquare, Video } from 'lucide-react';
import { ContractOverview } from './ContractOverview';
import { Timeline } from './Timeline';
import { TaskList } from './TaskList';
import { DocumentCenter } from './DocumentCenter';
import { MessagingCenter } from './MessagingCenter';
import { MeetingCenter } from './MeetingCenter';

interface Contract {
  id: string;
  title: string;
  agency: string;
  value: number;
  startDate: string;
  endDate: string;
  status: 'active' | 'completed' | 'pending';
  description: string;
}

export function ContractSpace() {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedContract, setSelectedContract] = useState<Contract | null>({
    id: '1',
    title: 'IT Infrastructure Modernization',
    agency: 'Department of Defense',
    value: 2500000,
    startDate: '2024-01-14',
    endDate: '2024-12-30',
    status: 'active',
    description: 'Comprehensive IT infrastructure upgrade including cloud migration and security enhancements.',
  });

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FileText },
    { id: 'timeline', name: 'Timeline', icon: Calendar },
    { id: 'tasks', name: 'Tasks', icon: CheckCircle },
    { id: 'documents', name: 'Documents', icon: FileArchive },
    { id: 'messages', name: 'Messages', icon: MessageSquare },
    { id: 'meetings', name: 'Meetings', icon: Video },
  ];

  return (
    <div className="space-y-6">
      {/* Contract Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">{selectedContract?.title}</h2>
          <p className="mt-1 text-sm text-gray-500">{selectedContract?.agency}</p>
        </div>
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${
            selectedContract?.status === 'active'
              ? 'bg-green-100 text-green-800'
              : selectedContract?.status === 'completed'
              ? 'bg-blue-100 text-blue-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          Active
        </span>
      </div>

      {/* Contract Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group relative flex items-center space-x-2 border-b-2 px-1 pb-4 pt-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                <Icon className="h-5 w-5" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <ContractOverview />}
        {activeTab === 'timeline' && <Timeline contractId={selectedContract?.id || ''} />}
        {activeTab === 'tasks' && <TaskList contractId={selectedContract?.id || ''} />}
        {activeTab === 'documents' && <DocumentCenter contractId={selectedContract?.id || ''} />}
        {activeTab === 'messages' && <MessagingCenter contractId={selectedContract?.id || ''} />}
        {activeTab === 'meetings' && <MeetingCenter contractId={selectedContract?.id || ''} />}
      </div>
    </div>
  );
}